"use client";

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { SupportedLanguage } from "@/lib/shared";
import { getCookieSync } from "@/lib/cookies";
import {
  getMyEducations,
  getMyExperiences,
  getMyLanguages,
  getMyProfile,
  getMyProjects,
  getMySkills,
} from "@/actions/profiles";
import { getJobById, getMyTasks } from "@/actions/jobs";

/**
 * SPA: تحميل مسبق لـ chunks خطوات التقديم + تسخين كاش React Query
 * حتى يبدو الانتقال بين الخطوات فورياً؛ الإرسال النهائي يبقى في صفحة المراجعة.
 */
export default function ApplyJobFlowPrefetch({
  locale,
}: {
  locale: SupportedLanguage;
}) {
  const queryClient = useQueryClient();
  const location = useLocation();

  useEffect(() => {
    void import("@/pages/ApplyJobStartRoute");
    void import("@/pages/ApplyJobExperienceRoute");
    void import("@/pages/ApplyJobEducationSkillsLanguageRoute");
    void import("@/pages/ApplyJobVideosRoute");
    void import("@/pages/ApplyJobProjectRoute");
    void import("@/pages/ApplyJobSalaryRoute");
    void import("@/pages/ApplyJobReviewRoute");
    void import("@/pages/ApplyJobLoginRoute");
    void import("@/pages/ApplyJobUnknownStepPage");

    const params = new URLSearchParams(location.search);
    const jobId =
      params.get("jobId")?.trim() || getCookieSync("JobId")?.trim() || undefined;

    void queryClient.prefetchQuery({
      queryKey: ["myProfile", "apply-job-entry"],
      queryFn: () => getMyProfile(),
    });
    void queryClient.prefetchQuery({
      queryKey: ["myExperiences", "apply-job"],
      queryFn: () => getMyExperiences(),
    });
    void queryClient.prefetchQuery({
      queryKey: ["myEducations", "apply-job"],
      queryFn: () => getMyEducations(),
    });
    void queryClient.prefetchQuery({
      queryKey: ["mySkills", "apply-job"],
      queryFn: () => getMySkills(),
    });
    void queryClient.prefetchQuery({
      queryKey: ["myLanguages", "apply-job"],
      queryFn: () => getMyLanguages(),
    });
    void queryClient.prefetchQuery({
      queryKey: ["myProjects", "apply-job"],
      queryFn: () => getMyProjects(),
    });

    if (!jobId) return;

    void (async () => {
      try {
        const jobRes = await getJobById(jobId);
        queryClient.setQueryData(["job", "apply-job", jobId], jobRes);
        queryClient.setQueryData(["job", "apply-job-videos", jobId], jobRes);
        queryClient.setQueryData(["job", "apply-job-salary", jobId], jobRes);
        queryClient.setQueryData(["job", "apply-job-review", jobId], jobRes);
      } catch {
        /* تجاهل أخطاء التحميل المسبق */
      }
    })();

    void (async () => {
      try {
        const tasksRes = await getMyTasks(jobId);
        queryClient.setQueryData(
          ["myTasks", jobId, "apply-job-videos"],
          tasksRes,
        );
        queryClient.setQueryData(
          ["myTasks", jobId, "apply-job-review"],
          tasksRes,
        );
      } catch {
        /* تجاهل */
      }
    })();
  }, [queryClient, location.pathname, location.search, locale]);

  return null;
}
